import React from 'react';
import {
  Alert, AlertTriangle, Badge, Card, CardHeader, CreditCard01, EmptyState, FileX02, LineChartDown01, Receipt, ReverseLeft, Skeleton,
} from '@opendate-app/setlist';

const SEVERITY = {
  error: { bg: 'var(--sl-bg-error-primary)', fg: 'var(--sl-text-error)' },
  warning: { bg: 'var(--sl-bg-warning-primary)', fg: 'var(--sl-text-warning)' },
  brand: { bg: 'var(--sl-bg-brand-primary)', fg: 'var(--sl-text-brand)' },
  gray: { bg: 'var(--sl-bg-tertiary)', fg: 'var(--sl-text-secondary)' },
};

const wholeUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const plural = (n, one, many) => (n === 1 ? one : many);

export function listNames(names, count) {
  const extra = count - names.length;
  if (extra > 0) return `${names.join(', ')}${names.length > 1 ? ',' : ''} and ${extra} more`;
  if (names.length === 2) return names.join(' and ');
  return names.join(', ');
}

const ROWS = {
  chargebacks: {
    severity: 'error',
    icon: AlertTriangle,
    cta: 'Respond',
    title: (r) => plural(r.count, '1 chargeback needs a response', `${r.count} chargebacks need a response`),
    detail: (r) => (r.due_on ? `Respond by ${shortDate(r.due_on)} · ` : '') + `${wholeUsd.format(r.amount)} at stake`,
  },
  behind_pace: {
    severity: 'warning',
    icon: LineChartDown01,
    cta: 'Review',
    title: (r) => `${r.count} ${plural(r.count, 'show', 'shows')} pacing behind forecast`,
    detail: (r) => listNames(r.names, r.count),
  },
  unpaid_settlements: {
    severity: 'warning',
    icon: Receipt,
    cta: 'Review',
    title: (r) => `${r.count} ${plural(r.count, 'settlement', 'settlements')} unpaid`,
    detail: (r) => listNames(r.names, r.count),
  },
  refund_requests: {
    severity: 'brand',
    icon: ReverseLeft,
    cta: 'Review',
    title: (r) => `${r.count} ${plural(r.count, 'refund request', 'refund requests')} pending`,
    detail: (r) => `Oldest submitted ${shortDate(r.oldest_on)}`,
  },
  missing_w9: {
    severity: 'gray',
    icon: FileX02,
    cta: 'Review',
    title: (r) => `${r.count} upcoming ${plural(r.count, 'show', 'shows')} missing a W-9`,
    detail: (r) => listNames(r.names, r.count),
  },
  failed_pledge_payments: {
    severity: 'error',
    icon: CreditCard01,
    cta: 'Review',
    title: (r) => `${r.count} pledge ${plural(r.count, 'payment', 'payments')} failed`,
    detail: (r) => listNames(r.names, r.donor_count ?? r.count),
  },
};

const ROW_STYLE = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: '1px solid var(--sl-border-secondary)', textDecoration: 'none',
};

function AttentionRow({ row }) {
  const config = ROWS[row.key];
  const { bg, fg } = SEVERITY[config.severity];
  const Icon = config.icon;

  return (
    <a href={row.path} style={ROW_STYLE}>
      <span style={{ width: 32, height: 32, borderRadius: '50%', background: bg, color: fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon width={16} height={16} aria-hidden="true" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{config.title(row)}</span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--sl-text-tertiary)' }}>{config.detail(row)}</span>
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sl-text-brand)', whiteSpace: 'nowrap' }}>{config.cta}</span>
    </a>
  );
}

function NeedsAttentionCard({ rows, loading, error, allRooms }) {
  const visible = (rows || []).filter((row) => ROWS[row.key]);

  return (
    <Card header={<CardHeader title="Needs attention" supportingText={allRooms ? 'Not filtered by room' : undefined} badge={visible.length > 0 ? <Badge color="error" size="sm">{visible.length}</Badge> : null} />}>
      {loading && Array.from({ length: 5 }, (_, i) => <Skeleton key={i} width="100%" height={40} style={{ marginBottom: 12 }} />)}
      {!loading && error && <Alert color="error" title="Couldn't load what needs attention" description="Refresh the page to try again." />}
      {!loading && !error && visible.length === 0 && <EmptyState size="sm" title="Nothing needs attention" />}
      {!loading && !error && visible.map((row) => <AttentionRow key={row.key} row={row} />)}
    </Card>
  );
}

export default NeedsAttentionCard;
